import axios from 'axios';
import { erebusHttp, init, legacyHttp } from '@erebus/http';
import { useConnectionStore } from 'stores/connection';
import { parseServerTiming } from '@erebus/telemetry';
import { startTimingTelemetry } from './telemetry';
import { NotFoundError } from 'src/services/errorHandlingService';
import { isCookieAuthMode } from './authMode';

/**
 * Default cap for a normal request. Without one, a request that never comes
 * back never rejects, and the UI waits forever with no error and no retry.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Longer cap for file uploads and downloads, where the body itself legitimately
 * takes time to transfer. Axios counts the whole exchange against the timeout,
 * so the default would abort a large file on a slow connection.
 */
export const FILE_TRANSFER_TIMEOUT_MS = 120_000;

// The instances come from @erebus/http. These are the names the app has
// always used for them.
const api = legacyHttp;
const erebus = erebusHttp;

api.defaults.timeout = REQUEST_TIMEOUT_MS;
api.defaults.paramsSerializer = {
  serialize: (params) => {
    const parts = [];

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
        });
      } else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });

    return parts.join('&');
  },
};

erebus.defaults.timeout = REQUEST_TIMEOUT_MS;
erebus.defaults.paramsSerializer = (params) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => queryParams.append(key, item));
    } else {
      queryParams.append(key, value);
    }
  });

  return queryParams.toString();
};

/**
 * Sets the base URLs from runtime config and sets up auth for the mode
 * `chooseAuthMode()` picked (boot/authMode).
 *
 * Bearer mode sends the auth store's token as a Bearer header on every
 * request, to both APIs. An empty token sends no header, so a logged-out user
 * sends none. Cookie mode sends the session cookie to Go, and `@erebus/http`
 * attaches the Java token it holds in memory.
 *
 * Call in main.ts after loadRuntimeConfig() and chooseAuthMode(), and before
 * any API call.
 *
 * @param {{ token: string }} authStore - The Pinia auth store
 */
export function initAxios(authStore) {
  if (isCookieAuthMode()) {
    init();
  } else {
    init({ bearerToken: () => authStore.token });
  }
}

/**
 * Builds the message for a 404 rejection.
 *
 * The query string is dropped on purpose. It can carry whatever a user typed
 * into a search box, and this message may end up in a log.
 *
 * @param {Object} err - The rejected axios error
 * @return {string} A message naming the method and path that 404'd
 */
function notFoundMessage(err) {
  const method = (err.config?.method ?? 'request').toUpperCase();
  const path = (err.config?.url ?? 'unknown path').split('?')[0];
  return `Not found: ${method} ${path}`;
}

/**
 * A call slower than this gets a console warning.
 *
 * Matches `SLOW_QUERY_THRESHOLD_MS` in the legacy Java app, so a warning here
 * and a slow-query line in the server log mean the same thing.
 */
export const SLOW_API_CALL_MS = 1000;

/**
 * How many recent calls are kept. At current polling rates this is roughly the
 * last twenty minutes - enough for support to ask a user what they were doing
 * when it got slow, without growing without bound in a long-lived tab.
 */
export const API_TIMING_BUFFER_SIZE = 50;

/**
 * One measured API call.
 *
 * @typedef {Object} ApiCallTiming
 * @property {string} at - When the call finished, as an ISO timestamp
 * @property {string} method - The HTTP method, uppercased
 * @property {string} path - The request path, without its query string
 * @property {number} durationMs - Wall time from request to response
 * @property {string} backend - Which client made the call, 'erebus' or 'legacy'
 * @property {string|null} traceId - The server's correlation id, null if none
 */

/** @type {Array<ApiCallTiming>} Recent timings, oldest first. Stays in the browser. */
let apiTimings = [];

/**
 * The telemetry collector, once setup has run. Null before that, and null is a
 * normal state - the buffer and console warning above work without it.
 *
 * @type {{ record: Function, stop: Function } | null}
 */
let timingCollector = null;

/**
 * Recent API call timings, oldest first.
 *
 * Also reachable as `window.__apiTimings()` so support can ask a user to read
 * it out of the browser console.
 *
 * @return {Array<ApiCallTiming>} A copy, so a caller cannot corrupt the buffer
 */
export function recentApiCalls() {
  return [...apiTimings];
}

/**
 * Records how long one API call took, and warns if it was slow.
 *
 * The query string is dropped for the same reason as in `notFoundMessage` - it
 * can carry whatever a user typed into a search box, and this buffer is read
 * out to support.
 *
 * A call with no config cannot be measured: a request interceptor threw before
 * the start time was stamped, so there is nothing to subtract from.
 *
 * @param {Object} config - The axios config, stamped by handleRequest
 * @param {Object} [response] - The response, absent when none arrived
 * @param {string} backend - Which client made the call, 'erebus' or 'legacy'
 * @param {{ status: number, errorKind: string }} [failure] - Set when the call failed.
 *   The collector keeps it only if the user is not signed in.
 */
function recordApiCall(config, response, backend, failure) {
  const startedAt = config?.metadata?.startedAt;
  if (startedAt === undefined) {
    return;
  }

  const method = (config.method ?? 'request').toUpperCase();
  const path = (config.url ?? 'unknown path').split('?')[0];
  const durationMs = Math.round(performance.now() - startedAt);
  // Axios lowercases response header names, so this is the only spelling that
  // works - not the 'X-Trace-ID' the backend actually sends.
  const traceId = response?.headers?.['x-trace-id'] ?? null;

  apiTimings.push({ at: new Date().toISOString(), method, path, durationMs, backend, traceId });
  if (apiTimings.length > API_TIMING_BUFFER_SIZE) {
    apiTimings.shift();
  }

  if (durationMs >= SLOW_API_CALL_MS) {
    const trace = traceId === null ? '' : ` trace=${traceId}`;
    console.warn(`Slow API call (${durationMs}ms): ${method} ${path} [${backend}]${trace}`);
  }

  // The collector decides for itself whether to keep this. It is given the raw
  // url rather than the stripped path because it needs the query string gone on
  // its own terms and the full url to find the browser's resource entry.
  timingCollector?.record({
    url: config.url ?? path,
    method,
    backend,
    totalMs: durationMs,
    traceId,
    // Only the Go backend sends Server-Timing. Null for legacy, which is
    // different from zero and is recorded that way.
    serverMs: parseServerTiming(response?.headers?.['server-timing']),
  });

  if (failure) {
    timingCollector?.recordError({
      url: config.url ?? path,
      method,
      backend,
      status: failure.status,
      errorKind: failure.errorKind,
      totalMs: durationMs,
      traceId,
    });
  }
}

/**
 * Works out how a failed call failed, for the pre-login error report.
 *
 * No response is a network failure, or a timeout when axios says it gave up
 * waiting. A response with an error status is an http failure. A call the app
 * cancelled itself is not a failure, so it returns null.
 *
 * @param {Object} err - The axios error
 * @return {{ status: number, errorKind: string } | null}
 */
function describeFailure(err) {
  if (err.code === 'ERR_CANCELED') {
    return null;
  }
  if (err.response) {
    return { status: err.response.status, errorKind: 'http' };
  }
  const timedOut = err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT';
  return { status: 0, errorKind: timedOut ? 'timeout' : 'network' };
}

/**
 * Setup function to configure axios interceptors
 * Should be called from main.ts after stores and router are initialized
 * @param {Object} app - Vue app instance
 * @param {Object} router - Vue router instance
 * @param {Object} stores - Store instances (authStore)
 */
export function setupAxiosInterceptors(app, router, { authStore }) {
  const recentlyLoggedOut = () => {
    const now = new Date().getTime();
    const diff = now - authStore.signedOutTime;
    return diff < 5000;
  };

  /**
   * The 401/403 policy for cookie mode. Bearer mode keeps the one below.
   *
   * - A Go 401 means the session cookie is gone, so log out. Except under
   *   `/auth/*`: a wrong password or code, and logout itself, answer 401 there,
   *   and logging out on those would loop. And not once signed out already:
   *   calls still in flight at logout all come back as 401s.
   * - A Java 401 has already been retried once with a fresh token by
   *   `@erebus/http`. The session may still be good, so the caller shows its
   *   own error instead.
   * - A 403 means signed in but not allowed. Say so, and stay signed in.
   *   Not under `/auth/*`, whose caller (the login screens) says why itself.
   *
   * @param {Object} err - The rejected axios error
   * @param {string} backend - 'erebus' or 'legacy'
   * @param {number} status - 401 or 403
   */
  const handleCookieAuthRejection = (err, backend, status) => {
    const authRoute = (err.config.url ?? '').startsWith('/auth/');
    if (status === 403 && !authRoute) {
      app.config.globalProperties.$q.notify({
        message: 'You do not have access to some of the information requested.',
        type: 'negative',
      });
    } else if (status === 401 && backend === 'erebus' && !authRoute && authStore.sessionActive) {
      if (!recentlyLoggedOut()) {
        app.config.globalProperties.$q.notify({
          message: 'Your session has ended. Please log in again.',
          type: 'negative',
        });
      }
      authStore.authLogout().then(() => {
        router.push({ name: 'login' }).catch((error) => {
          console.dir(error);
        });
      });
    }
    return Promise.reject(err);
  };

  const handleRequest = (config) => {
    config.metadata = { startedAt: performance.now() };
    return config;
  };

  /**
   * Every branch below rejects. A response error interceptor that resolves
   * turns a failed request into a successful one, and the caller then reads
   * properties off a response that is `undefined`. The TypeError that follows
   * points at the caller instead of at the request that actually failed.
   */
  const handleRejection = (err, backend) => {
    if (err === undefined || err === null) {
      return Promise.reject(new Error('Request failed with no error to report'));
    }

    // Recorded before the branches below, so a call that failed is timed the
    // same as one that succeeded. A request that died locally has no response
    // and so no trace id, which is exactly the kind worth being able to see.
    recordApiCall(err.config, err.response, backend, describeFailure(err) ?? undefined);

    if (err.request === undefined || err.request === null) {
      // The request never went out - a request interceptor threw, or the
      // config was bad. There is no response to look at, so pass it straight on.
      return Promise.reject(err);
    }

    const connectionStore = useConnectionStore();

    if (err.request.status === 0) {
      // Network error - record failure, let store decide state.
      // A timeout also reports status 0, but it means the backend answered
      // slowly rather than being unreachable, so tag it separately.
      const kind =
        err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT' ? 'timeout' : 'unreachable';
      connectionStore.recordFailure(backend, kind);
      return Promise.reject(err);
    }

    const authStatus = err.response?.status ?? err.status;
    if (isCookieAuthMode() && err.config && (authStatus === 401 || authStatus === 403)) {
      return handleCookieAuthRejection(err, backend, authStatus);
    }

    if (
      (err.status === 401 ||
        err.status === 403 ||
        (err.response && (err.response.status === 401 || err.response.status === 403))) &&
      err.config &&
      !err.config.__isRetryRequest &&
      // A lookup the caller can live without (a person card) sets this, so a
      // refusal leaves that one item blank instead of logging the user out.
      // A referral doctor is refused people outside their case (PRX-2409).
      !err.config.skipAuthLogout
    ) {
      // if you ever get an unauthorized, logout the user
      if (err.config.url !== '/api/v1/authentication') {
        if (!recentlyLoggedOut()) {
          app.config.globalProperties.$q.notify({
            message:
              'You are not authorized to view some of the information requested. You may need to login again.',
            type: 'negative',
          });
        }
        // you can also redirect to /login if needed !
        authStore.authLogout().then(() => {
          router.push({ name: 'login' }).catch((error) => {
            console.dir(error);
          });
        });
      }

      return Promise.reject(err);
    }

    if (err.status === 404 || (err.response && err.response.status === 404)) {
      // A 404 is a real answer, so callers get a typed error they can catch.
      // errorHandlingService turns this into ErrorType.NotFound for the UI.
      return Promise.reject(new NotFoundError(notFoundMessage(err), err));
    }

    return Promise.reject(err);
  };

  const handleResponse = (response, backend) => {
    recordApiCall(response.config, response, backend);
    const connectionStore = useConnectionStore();
    connectionStore.recordSuccess(backend);
    return response;
  };

  // The buffer's life is one app boot, and so is this call. Resetting here
  // keeps a re-install from reporting timings taken against old interceptors.
  apiTimings = [];
  window.__apiTimings = recentApiCalls;

  // Same lifetime as the buffer. Stopping any previous collector keeps a
  // re-install from leaving an orphaned flush timer behind.
  timingCollector?.stop();
  timingCollector = startTimingTelemetry({ authStore });

  /*
   * In cookie mode @erebus/http retries a Java 401 once with a fresh token. The
   * retry runs through the whole interceptor chain, these handlers included,
   * and its outcome then comes back through them a second time as the original
   * call's. Each request is handled once, so the retry's outcome is timed,
   * counted and toasted once. The mark goes on the metadata handleRequest
   * stamped, which belongs to that one request.
   */
  const alreadyHandled = (config) => config?.metadata?.handled === true;
  const markHandled = (config) => {
    if (config?.metadata) config.metadata.handled = true;
  };

  const handleResponseOnce = (response, backend) => {
    if (alreadyHandled(response?.config)) return response;
    markHandled(response?.config);
    return handleResponse(response, backend);
  };

  const handleRejectionOnce = (err, backend) => {
    if (alreadyHandled(err?.config)) return Promise.reject(err);
    markHandled(err?.config);
    return handleRejection(err, backend);
  };

  // Register interceptors
  erebus.interceptors.request.use(handleRequest);
  api.interceptors.request.use(handleRequest);
  erebus.interceptors.response.use(
    (res) => handleResponseOnce(res, 'erebus'),
    (err) => handleRejectionOnce(err, 'erebus'),
  );
  api.interceptors.response.use(
    (res) => handleResponseOnce(res, 'legacy'),
    (err) => handleRejectionOnce(err, 'legacy'),
  );

  // Attach to Vue app for Options API compatibility
  app.config.globalProperties.$axios = axios;
  app.config.globalProperties.$api = api;
  app.config.globalProperties.$medService = erebus;
}

export { axios, api, erebus };
